import { clsx, type ClassValue } from 'clsx'
import { Recipe } from 'graphql/generated/graphql'
import { twMerge } from 'tailwind-merge'
import { jwtDecode } from "jwt-decode";
import { UserData } from 'types/common';
import Cookies from "js-cookie";
import { getStore } from 'features/store';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function detectScrolled() {
  let scrollPercentage =
    (window.scrollY /
      (document.documentElement.scrollHeight - window.innerHeight)) *
    100

  if (scrollPercentage >= 50) {
    return true
  } else return false
}

export function areAllValuesEmpty(obj: Record<string, any>): boolean {
  const objWithoutMiles = { ...obj } // Create a copy of the object without the "miles" key
  delete objWithoutMiles['miles'] // Remove the "miles" key from the copy

  for (const key in objWithoutMiles) {
    const value = objWithoutMiles[key]
    if (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0) &&
      !(typeof value === 'object' && Object.keys(value).length === 0)
    ) {
      return false // If any non-empty value (excluding "miles") is found, return false
    }
  }

  return true // All values (excluding "miles") are empty
}

export const getToken = () => {
  const { jwt_token } = getStore().getState().culineerAuth
  return jwt_token
}

export const getUserId = () => {
  return getStore().getState().culineerAuth.user?.user_id
}

export const greeting = () => {
  const time = new Date().getTime()
  if(time >= 0 && time < 12) {
    return 'Morning'
  } else if (time > 12 && time < 16) {
    return 'Afternoon'
  } else if (time > 16 && time < 20) {
    return 'Evening'
  } else {
    return 'Day'
  }
}

export const convertImageLinkToFile = async (imageUrl: string): Promise<{file: File, url: string}> => {
  const fileName = imageUrl.split('/')[imageUrl.split('/').length - 1].split('?')[0]
  const response = await fetch(`${process.env.NEXT_PUBLIC_API_ENDPOINT!}/proxy?url=${encodeURIComponent(imageUrl)}`);
  const blob = await response.blob();
  const file = new File([blob], fileName, { type: blob.type });
  const url = URL.createObjectURL(file).split('blob:')[1]
  return {
    url: url,
    file
  };
};

export const getSpeedLabel = (speed: number) => {
  if (speed < 1.4) return 'walking'; // Average walking speed ~1.4 m/s
  if (speed < 3.0) return 'running'; // Average running speed ~3.0 m/s
  return 'driving';
};

const toRadians = (degrees: number) => degrees * (Math.PI / 180);

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 3958.8; // Radius of the Earth in miles
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in miles
  return distance;
};

export const calculateTime = (distance: number, activity: string): number => {
  let speed; // Speed in km/h
  switch (activity) {
    case 'walking':
      speed = 5;
      break;
    case 'running':
      speed = 10;
      break;
    case 'driving':
      speed = 60;
      break;
    default:
      speed = 5; // Default to walking speed
  }
  return (distance / speed) * 60; // Convert hours to minutes
};

// Function to convert a minutes to hours and minutes
export const convertMinutesToHoursAndMinutes = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}hrs ${minutes}min`;
}

// Function to convert number to two digits number 
export const padToTwoDigits = (number: number): string => {
  return number.toString().padStart(2, '0');
}


// Function to convert average rating to a 5-star system
export const computeAverageRatingToStars = (ratings: { recipe_id: number, rating: number }[], maxStars: number = 5): number => {
  const sum = ratings.reduce((acc, val) => acc + val.rating, 0)
  const averageRating = sum / ratings.length

  return Math.floor((averageRating / 5) * maxStars);
}

export const removeTypename = (recipe: Recipe) => {
  const { __typename, ...rest } = recipe;
  
  return rest;
};

export const base64ToFile = (base64String: string, filename: string): File => {
  if (!base64String || typeof base64String !== 'string') {
    return new File([], filename);
  }
  // Separate the Base64 data from its metadata
  const [meta, data] = base64String.split(',');
  const mime = meta.match(/:(.*?);/)?.[1];
  
  // if (!mime) {
  //   throw new Error("Invalid Base64 string format");
  // }

  // Decode the Base64 string and create a byte array
  const binaryString = atob(data);
  const len = binaryString.length;
  const uInt8Array = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    uInt8Array[i] = binaryString.charCodeAt(i);
  }

  // Create and return a File object
  return new File([uInt8Array], filename, { type: mime });
}

export const isTokenExpired = (token: any) => {
    try {
        // Decode the token
        const decodedToken = token ? jwtDecode(token) : '';

        // Get the expiration time
        const exp = decodedToken ? decodedToken.exp : ''; // exp is in seconds

        // Get the current time
        const currentTime = Date.now() / 1000; // Convert to seconds

        // Check if the token is expired
        return  exp && exp < currentTime; // Returns true if expired, false if valid
    } catch (error) {
        console.error("Failed to decode token:", error);
        return true; // In case of an error, consider the token expired
    }
};


export const isNotValidURL = (url: string): boolean => {
  const REGEX_PATTERN = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;

  if (REGEX_PATTERN.test(url)) {
    return false;
  }

  return true;
}

export const findActiveRole = (roles?: Record<string, any>[] | null): string | undefined => {
  if (!roles || !Array.isArray(roles) || roles.length === 0) return undefined
  const activeRole = roles.find((role) => role?.default) || roles[0]
  return activeRole?.role?.name?.toLowerCase() || activeRole?.name?.toLowerCase()
}

/**
 * Checks if a user has the 'admin' role and does not have any other roles.
 */
export const isOnlyAdmin = (user: any): boolean => {
  if (!user) return false

  const rawRoles = user?.userRoles || user?.roles
  if (Array.isArray(rawRoles)) {
    // The roles list is the truth. An empty list means no access (e.g. an admin whose role was
    // removed), even if the account is still labelled user_type "Admin".
    const roleNames = rawRoles
      .map((r: any) => (r?.role?.name || r?.name || '')?.toLowerCase())
      .filter(Boolean)

    return roleNames.includes('admin') && roleNames.every((name: string) => name === 'admin')
  }

  // Fallback to user_type only when no roles list was loaded at all
  if (user?.user_type?.toLowerCase() === 'admin') {
    return true
  }

  return false
}


/**
 * True when the account holds the admin role, whichever role it is currently switched to.
 * Used to keep everyone else out of the /admin screens (the API checks this on every call too).
 */
export const hasAdminRole = (user: any): boolean => {
  if (!user) return false

  const rawRoles = user?.userRoles || user?.roles
  if (Array.isArray(rawRoles)) {
    return rawRoles.some((r: any) => (r?.role?.name || r?.name || '')?.toLowerCase() === 'admin')
  }

  // Only when no roles list was loaded at all
  return user?.user_type?.toLowerCase() === 'admin'
}


export const filterProducerSteps = (steps: any, membershipType: string) => {
  if(membershipType === 'Basic') {
    const filterSteps = steps.filter((_: any, index: number) => ![3, 4, 5].includes(index));
    return filterSteps;
  }
  
  return steps;
}

export const filterMarketSteps = (steps: any, membershipType: string) => {
  if(membershipType === 'Basic') {
    const filterSteps = steps.filter((_: any, index: number) => ![3].includes(index));
    return filterSteps;
  }

  return steps;
}

export const checkPermission = (permission: string): boolean => {
  if(!permission) return false;
  if(permission === 'Basic' || permission.toLowerCase() === 'verified business') return false;
  return true
}

/**
 * Check if user can access product management features
 * Allows both Verified Business (free tier) and Enhanced (paid) tiers
 * Note: Basic tier doesn't have in-app access, so not relevant here
 *
 * @param membership_type - User's membership type
 * @param enhanced - Whether user has enhanced flag set
 * @returns true if user can access product features
 */
export const canAccessProducts = (membership_type: string | null | undefined, enhanced: boolean): boolean => {
  if (enhanced) return true
  if (!membership_type) return false
  const normalizedType = membership_type.toLowerCase()
  return normalizedType === 'verified business' || normalizedType === 'enhanced' || normalizedType === 'professional'
}

export const checkIsVerified = (verified: boolean): boolean => {
  if(typeof verified === "boolean" && verified) return true;
  return false
}

export const getPlatformNameFromURL = (url: string) => {
  const hostname = new URL(url).hostname;

  const domainParts = hostname.split('.');
  const domain = domainParts.slice(-2).join('.');

  const platformMapping: Record<string, any> = {
      'facebook.com': 'Facebook',
      'instagram.com': 'Instagram',
      'twitter.com': 'Twitter',
      'x.com': 'Twitter',
      'linkedin.com': 'LinkedIn',
      'youtube.com': 'YouTube',
  };

  return platformMapping[domain] || 'Unknown Platform';
};

export const urlExtractor = (url: string): Record<string, string> => {
  const params: Record<string, any> = {};
  const regex = /[?&]([^=]+)=([^&]+)/g;

  let match;
  while ((match = regex.exec(url)) !== null) {
    params["type"] = match[1]
    params["value"] = match[2]; 
  }

  return params
}

export const capitalizeFirstLetter = (word: string) => {
  if (!word) return ''; 
  return word.charAt(0).toUpperCase() + word.slice(1);
}

const daysOfWeek: any = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6
};

export const sortByDay = (a: any, b: any) => {
  return daysOfWeek[a.day] - daysOfWeek[b.day];
};

export const ingredientTypesArr: string[] = [
  'Fruit',
  'Vegetable',
  'Meat',
  'Dairy',
  'Eggs',
  'Flowers',
  'Herbs and Spices',
  'Nuts',
  'Packages goods',
  'Poultry',
  'Seafood',
  'Seeds and Grains',
  'Sweeteners',
  'Mushrooms'
]

export const sortByLongitude = (a: any, b: any) => {
  if (a.longitude !== b.longitude) {
    return a.longitude - b.longitude;
  }
  return a.latitude - b.latitude;
};

export const sortByLatitude = (a: any, b: any) => {
  if (a.latitude !== b.latitude) {
    return a.latitude - b.latitude;
  }
  return a.longitude - b.longitude;
};

export const sortByLatitudeStable = (a: any, b: any) => {
  const latDiff = a.longitude - b.longitude;
  if (latDiff !== 0) return latDiff;

  return new Intl.Collator().compare(a.latitude.toString(), b.latitude.toString());
};

export const sortByDistance = (a: any, b: any) => {
  return a.distance - b.distance
}

export const isEnhancedListing = (l: any): boolean => {
  const PREMIUM = ['enhanced', 'professional', 'verified business']
  return l?.enhanced === true && PREMIUM.includes((l?.membership_type ?? '').toLowerCase())
}

export const sortByEnhancedThenDistance = (a: any, b: any) => {
  const aEnhanced = isEnhancedListing(a)
  const bEnhanced = isEnhancedListing(b)
  if (aEnhanced !== bEnhanced) return aEnhanced ? -1 : 1
  return parseFloat(a.distance ?? 0) - parseFloat(b.distance ?? 0)
}

// Fisher-Yates shuffle — works on any array
export const shuffle = <T>(arr: T[]): T[] => {
  const result = arr.slice()
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

// Shuffles Enhanced listings randomly (for discover page variety),
// then appends Verified Business listings sorted by distance.
export const shuffleEnhancedListings = (listings: any[]): any[] => {
  const enhanced = shuffle(listings.filter(l => l.enhanced === true))
  const others = listings.filter(l => l.enhanced !== true).sort((a, b) => a.distance - b.distance)
  return [...enhanced, ...others]
}

export const capitalizeWords = (sentence: string): string => {
  return sentence
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
}

export const transformSuggestedListingObject = (item: any): UserData => {
  return {
    listing: item,
    user_id: item?.user_id || item?.user?.user_id,
    slug: item?.slug,
    full_name: item?.name ?? item.user?.full_name,
    user_type: item.user_type,
    profile_photo_resource: item?.user?.profile_photo_resource ?? item?.photo,
    membership_type: item?.membership_type,
  }
}

export const transformSuggestedUserObject = (item: any): UserData => {
  return {
    listing: item?.default_listing,
    user_id: item?.user_id || item?.default_listing?.user_id,
    slug: item?.user_type?.toLowerCase() === 'general' ? item?.slug : item?.default_listing?.slug,
    full_name: item?.default_listing?.name ?? item.full_name,
    user_type: item?.user_type?.toLowerCase() === 'general' ? item?.user_type : (item?.default_listing?.user_type || item?.user_type),
    profile_photo_resource: item?.default_listing?.photo || item?.profile_photo_resource,
    membership_type: item?.default_listing?.membership_type || item?.membership_type,
  }
}

export const transformUserObject = (item: any): UserData => {
  return {
    listing: item?.default_listing,
    user_id: item?.user_id || item?.default_listing?.user_id || item?.user?.user_id,
    slug:   item?.user_type?.toLowerCase() === 'general' ? item?.slug : item?.default_listing?.slug,
    full_name: item?.default_listing?.name ?? item?.full_name ?? item?.name,
    user_type: item?.user_type?.toLowerCase() === 'general' ? item?.user_type : (item?.default_listing?.user_type || item?.user_type),
    profile_photo_resource: item?.default_listing?.photo || item?.profile_photo_resource || item?.photo,
    membership_type: item?.default_listing?.membership_type || item?.membership_type,
  }
}

export const generateReferralCode = (length: number): string => {
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let code = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    code += charset[randomIndex];
  }
  return code;
}

export const checkString = (str: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (emailRegex.test(str)) {
    return true;
  } else if (str?.length === 6) {
    return true;
  } else {
    return false;
  }
}

export const NextThreeMonths = () => {
  const now = new Date();
  const threeMonthsLater = new Date();
  threeMonthsLater.setMonth(now.getMonth() + 3);

  // Format as YYYY-MM-DD
  const formattedDate = threeMonthsLater.toISOString().split('T')[0];

  return formattedDate
}

export const dateAfterMonths = (months: number): Date => {
  const today = new Date();
  const futureDate = new Date(today);
  futureDate.setMonth(today.getMonth() + months);

  if (futureDate.getDate() < today.getDate()) {
    futureDate.setDate(0);
  }

  return futureDate;
};

export const blobToFile = (blob: Blob, fileName: string, mimeType = blob.type): File => {
  return new File([blob], fileName, { type: mimeType, lastModified: Date.now() });
};

export const isTrulyEmpty = (value: unknown): boolean => {
  if (value == null) return true; 

  if (typeof value === "string" || Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    return Object.keys(value).length === 0;
  }

  return false;
}

// Save user in cookie (client-side)
export function setUserCookie(user: any) {
  Cookies.set("user", JSON.stringify(user), {
    expires: 7, // days
    path: "/",  // available everywhere
    secure: true,
    sameSite: "lax",
  });
}

// Remove cookie
export function clearUserCookie() {
  Cookies.remove("user");
}

export const getUniqueIngredients = (ingredients: string[]): string[] => {
  return [...new Set(ingredients?.map(item => item?.trim().toLowerCase()))];
}

export const formatWithTimezoneOffset = (scheduledDate: string, scheduledTime: string): string => {
  const d = new Date(`${scheduledDate}T${scheduledTime}`);
  const offset = -d.getTimezoneOffset(); // minutes
  const sign = offset >= 0 ? "+" : "-";
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, "0");

  // hours and minutes from offset
  const hours = pad(offset / 60);
  const minutes = pad(offset % 60);

  // add ":00" seconds before timezone
  return `${scheduledDate}T${scheduledTime}:00${sign}${hours}:${minutes}`;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

// Formats the stored whole-number/fraction pair into what the free-text quantity field should display, e.g. ("1", "1/2") -> "1 1/2"
export const formatQuantityDisplay = (whole?: string | number | null, fraction?: string | null): string => {
  const wholeStr = whole !== undefined && whole !== null && `${whole}` !== '0' && `${whole}` !== '' ? `${whole}` : '';
  const fractionStr = fraction || '';
  if (wholeStr && fractionStr) return `${wholeStr} ${fractionStr}`;
  if (wholeStr) return wholeStr;
  if (fractionStr) return fractionStr;
  return '';
}

// Parses what a user typed into the free-text quantity field (e.g. "1 1/2", "3/4", "2", "0.25") into the same
// whole-number/fraction shape the app already stores. Returns null when the text isn't a recognizable quantity.
export const parseQuantityInput = (raw: string): { whole: string; fraction: string } | null => {
  const trimmed = raw.trim();
  if (!trimmed) return { whole: '', fraction: '' };

  const parts = trimmed.split(/\s+/);
  if (parts.length > 2) return null;

  let wholePart = '';
  let fractionPart = '';

  if (parts.length === 2) {
    if (!/^\d+$/.test(parts[0]) || !/^\d+\/\d+$/.test(parts[1])) return null;
    wholePart = parts[0];
    fractionPart = parts[1];
  } else {
    const token = parts[0];
    if (/^\d+\/\d+$/.test(token)) {
      fractionPart = token;
    } else if (/^\d+$/.test(token)) {
      wholePart = token;
    } else if (/^\d*\.\d+$/.test(token)) {
      const value = parseFloat(token);
      const whole = Math.floor(value);
      const remainder = value - whole;
      if (whole > 0) wholePart = `${whole}`;
      if (remainder > 0.001) {
        const denominator = 100;
        let numerator = Math.round(remainder * denominator);
        const divisor = gcd(numerator, denominator);
        numerator = numerator / divisor;
        fractionPart = `${numerator}/${denominator / divisor}`;
      }
    } else {
      return null;
    }
  }

  if (fractionPart) {
    const [n, d] = fractionPart.split('/').map(Number);
    if (d === 0 || n === 0) return null;
    if (n >= d) {
      const extraWhole = Math.floor(n / d);
      const remainderN = n % d;
      wholePart = `${parseInt(wholePart || '0', 10) + extraWhole}`;
      fractionPart = remainderN > 0 ? `${remainderN}/${d}` : '';
    }
  }

  return { whole: wholePart, fraction: fractionPart };
}
